import { WritingTextarea } from "../../writing/writing-field";
import { tokenizeChatLinks, truthfulChatMessage } from "./chat-links";
import { ChatCamera } from "./chat-camera";
import { prepareChatImage } from "../../api/chat-image";
import { useEffect, useRef, useState, type FormEvent } from "react";
import {
  AiClientError,
  sendAiChat,
  synthesizeAiSpeech,
  synthesizeAiSpeechDetailed,
  type AiVoiceLanguage,
  type AiVoiceProfile
} from "../../api/ai-client";
import type { AiChatMessage, AiChatResponse } from "../../api/ai.types";
import { VOICE_PREVIEW_ES, verifiedLocalVoiceLabel } from "../../api/natural-voice";
import { requestOmniAnticipation, type AnticipationMode } from "../../api/anticipation-client";
import {
  anticipationDelay, canAnticipateContinuation, canAnticipateTyping, keepsSameDraftPrefix, previewContext
} from "../../anticipation/anticipation-policy";
import { getOrCreateAuthSession } from "../../api/auth-client";
import type { AuthSessionResponse } from "../../api/auth.types";
import { mergeConversationHistory, rememberConversation, restoredConversationId } from "../../api/conversation-sync";
import {
  createConversation,
  getConversationMessages,
  listConversations,
  saveConversationMessage
} from "../../api/conversation-client";
import type { OmnisyniaClientModule } from "../module.types";
import { getActiveUltraOrganizationId } from "../../api/ultra-client";
import {
  deleteAllMemories,
  deleteMemory as deleteStoredMemory,
  getMemoryConsent,
  listMemories,
  saveMemory,
  setMemoryConsent,
  setConversationContinuity,
  updateMemory,
  type MemoryCategory,
  type MemoryRecord
} from "../../api/memory-client";

function MessageContent({ content, isAssistant }: { content: string; isAssistant: boolean }) {
  // Never rewrite a user message; the artifact integrity warning applies only to model answers.
  const safe = isAssistant ? truthfulChatMessage(content) : content;
  return <span style={{ whiteSpace: "pre-wrap" }}>{tokenizeChatLinks(safe).map((part, index) =>
    part.kind === "link" ? (
      <a key={index} href={part.href} target="_blank" rel="noopener noreferrer">{part.text}</a>
    ) : <span key={index}>{part.text}</span>
  )}</span>;
}

interface SharedLocation {
  latitude: number;
  longitude: number;
  accuracy: number;
  timezone: string;
  updatedAt: string;
}

type LocationStatus = "inactive" | "requesting" | "active" | "denied" | "unavailable";
type MicrophoneStatus = "inactive" | "listening" | "denied" | "unavailable" | "error";

interface SpeechRecognitionAlternativeLike {
  transcript: string;
}

interface SpeechRecognitionResultLike {
  [index: number]: SpeechRecognitionAlternativeLike;
  length: number;
}

interface SpeechRecognitionResultListLike {
  [index: number]: SpeechRecognitionResultLike;
  length: number;
}

interface SpeechRecognitionEventLike {
  results: SpeechRecognitionResultListLike;
}

interface SpeechRecognitionErrorEventLike {
  error: string;
}

interface BrowserSpeechRecognition {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  maxAlternatives: number;
  onresult: ((event: SpeechRecognitionEventLike) => void) | null;
  onerror: ((event: SpeechRecognitionErrorEventLike) => void) | null;
  onend: (() => void) | null;
  start(): void;
  stop(): void;
  abort(): void;
}

type SpeechRecognitionConstructor = new () => BrowserSpeechRecognition;

interface SpeechWindow extends Window {
  SpeechRecognition?: SpeechRecognitionConstructor;
  webkitSpeechRecognition?: SpeechRecognitionConstructor;
}

function detectVoiceLanguage(text: string): AiVoiceLanguage {
  const words = text.toLowerCase().match(/[a-záéíóúüñ']+/g) ?? [];
  const spanish = new Set(["el", "la", "los", "las", "de", "que", "para", "con", "una", "un", "es", "en", "por", "como", "puede"]);
  const english = new Set(["the", "a", "an", "of", "that", "for", "with", "is", "in", "by", "as", "can", "you", "your"]);
  const spanishScore = words.filter((word) => spanish.has(word)).length;
  const englishScore = words.filter((word) => english.has(word)).length;
  return englishScore > spanishScore ? "en" : "es";
}

function memoryCategoryLabel(category: MemoryCategory): string {
  return category === "project" ? "Proyecto" : "Sobre mí";
}

function ChatOmnisynia() {
  const greeting: AiChatMessage = {
    role: "assistant",
    content: "Hola, soy OMNISYNIA. Tengo una identidad digital neutral. Puedes preguntarme, pedirme una opinión razonada o crear algo conmigo."
  };
  const [input, setInput] = useState("");
  const [attachment, setAttachment] = useState<string | null>(null);
  const [imageBusy, setImageBusy] = useState(false);
  const [cameraOpen, setCameraOpen] = useState(false);
  const cameraInputRef = useRef<HTMLInputElement | null>(null);
  const galleryInputRef = useRef<HTMLInputElement | null>(null);
  const imageRequestRef = useRef(0);
  const [messages, setMessages] = useState<AiChatMessage[]>([greeting]);
  const [session, setSession] = useState<AuthSessionResponse | null>(null);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [sharedContinuity, setSharedContinuity] = useState(false);
  const sendingRef = useRef(false);
  const historyGenerationRef = useRef(0);
  const [isInitializing, setIsInitializing] = useState(true);
  const [lastResponse, setLastResponse] = useState<AiChatResponse | null>(null);
  const [anticipationEnabled, setAnticipationEnabled] = useState(() => {
    if (typeof window === "undefined") return true;
    try { return window.localStorage.getItem("omnisynia.anticipacion.enabled") !== "false"; }
    catch { return true; }
  });
  const [anticipationPreview, setAnticipationPreview] = useState<{
    mode: AnticipationMode;
    status: "preparing" | "ready" | "unavailable";
    text?: string;
  } | null>(null);
  const anticipationGenerationRef = useRef(0);
  const anticipationAbortRef = useRef<AbortController | null>(null);
  const lastAnticipationStartedAtRef = useRef(0);
  const activeAnticipationRef = useRef<{
    mode: AnticipationMode; draft: string; conversationId: string; token: string;
    controller: AbortController; version: number;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [memoryEnabled, setMemoryEnabled] = useState(false);
  const [memories, setMemories] = useState<MemoryRecord[]>([]);
  const [memoryCategory, setMemoryCategory] = useState<MemoryCategory>("user");
  const [memoryDraft, setMemoryDraft] = useState("");
  const [editingMemoryId, setEditingMemoryId] = useState<string | null>(null);
  const [memoryBusy, setMemoryBusy] = useState(false);
  const [memoryNotice, setMemoryNotice] = useState<string | null>(null);
  const [location, setLocation] = useState<SharedLocation | null>(null);
  const [locationStatus, setLocationStatus] = useState<LocationStatus>("inactive");
  const [microphoneStatus, setMicrophoneStatus] = useState<MicrophoneStatus>("inactive");
  const [voiceOutputEnabled, setVoiceOutputEnabled] = useState(false);
  const [voiceProfile, setVoiceProfile] = useState<AiVoiceProfile>(() => {
    if (typeof window === "undefined") return "feminine";
    return window.localStorage.getItem("omnisynia.voiceProfile") === "masculine"
      ? "masculine"
      : "feminine";
  });
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [voicePreviewNotice, setVoicePreviewNotice] = useState("");
  const locationWatchRef = useRef<number | null>(null);
  const speechRecognitionRef = useRef<BrowserSpeechRecognition | null>(null);
  const voiceInputRef = useRef(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const audioUrlRef = useRef<string | null>(null);
  const voiceRequestRef = useRef(0);
  // Updated in render so the prior effect's cleanup can distinguish
  // compatible new typing from an edit that changes the premise.
  const livePreviewContextRef = useRef({
    draft: input, conversationId, token: session?.sessionToken,
    active: !isLoading && !imageBusy && !attachment && !cameraOpen &&
      microphoneStatus !== "listening" && anticipationEnabled
  });
  livePreviewContextRef.current = {
    draft: input, conversationId, token: session?.sessionToken,
    active: !isLoading && !imageBusy && !attachment && !cameraOpen &&
      microphoneStatus !== "listening" && anticipationEnabled
  };

  useEffect(() => {
    void (async () => {
      try {
        const activeSession = await getOrCreateAuthSession();
        const available = await listConversations(activeSession.sessionToken);
        const restoredId = restoredConversationId(activeSession.user.id, available.map(c => c.id));
        let conversation = available.find(c => c.id === restoredId);
        if (!conversation) conversation = await createConversation(activeSession.sessionToken);
        const history = await getConversationMessages(activeSession.sessionToken, conversation.id);
        setSession(activeSession);
        setConversationId(conversation.id);
        rememberConversation(activeSession.user.id, conversation.id);
        setMessages(history.length ? history.map(({ role, content }) => ({ role, content })) : [greeting]);
        try {
          const [consent, storedMemories] = await Promise.all([
            getMemoryConsent(activeSession.sessionToken),
            listMemories(activeSession.sessionToken)
          ]);
          setMemoryEnabled(consent.includes("user") || consent.includes("project"));
          setSharedContinuity(consent.includes("conversation"));
          setMemories(storedMemories);
        } catch {
          setMemoryNotice("No se pudo cargar la memoria. El chat sigue disponible.");
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : "No se pudo cargar el historial.");
      } finally {
        setIsInitializing(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (!session || !conversationId) return;
    let active = true;
    let running = false;
    const refresh = async () => {
      if (!active || running || sendingRef.current || document.visibilityState === "hidden") return;
      running = true;
      const generation = historyGenerationRef.current;
      try {
        const [history, consent] = await Promise.all([
          getConversationMessages(session.sessionToken, conversationId), getMemoryConsent(session.sessionToken)
        ]);
        if (!active || sendingRef.current || generation !== historyGenerationRef.current) return;
        setMessages(current => {
          const next = mergeConversationHistory(history, current);
          if (!next.length) return current.length === 1 && current[0] === greeting ? current : [greeting];
          return JSON.stringify(next) === JSON.stringify(current) ? current : next;
        });
        setMemoryEnabled(consent.includes("user") || consent.includes("project"));
        setSharedContinuity(consent.includes("conversation"));
      } catch {
        // Keep the visible conversation and unsent draft if the device is offline.
      } finally { running = false; }
    };
    window.addEventListener("focus", refresh);
    window.addEventListener("online", refresh);
    document.addEventListener("visibilitychange", refresh);
    const timer = window.setInterval(refresh, 5_000);
    return () => {
      active = false;
      window.clearInterval(timer);
      window.removeEventListener("focus", refresh);
      window.removeEventListener("online", refresh);
      document.removeEventListener("visibilitychange", refresh);
    };
  }, [session?.sessionToken, conversationId]);

  useEffect(() => {
    return () => {
      if (locationWatchRef.current !== null) {
        navigator.geolocation.clearWatch(locationWatchRef.current);
      }
      imageRequestRef.current += 1;
      speechRecognitionRef.current?.abort();
      voiceRequestRef.current += 1;
      audioRef.current?.pause();
      if (audioUrlRef.current) URL.revokeObjectURL(audioUrlRef.current);
    };
  }, []);

  // Two clutches: an in-flight preview remains valid while the user adds
  // compatible words to the same partial sentence, but not after edits,
  // topic changes, a send, a session change or disabling anticipation.
  useEffect(() => {
    const active = activeAnticipationRef.current;
    const mode: AnticipationMode = input.trim() ? "typing" : "continuation";
    const available = Boolean(anticipationEnabled && session?.sessionToken &&
      conversationId && !isLoading && !imageBusy && !attachment && !cameraOpen &&
      microphoneStatus !== "listening");
    const allowed = available && (mode === "typing"
      ? canAnticipateTyping(input, messages)
      : canAnticipateContinuation(messages, Boolean(lastResponse)));

    if (allowed && mode === "typing" && active?.mode === "typing" &&
        active.token === session?.sessionToken && active.conversationId === conversationId &&
        !active.controller.signal.aborted && keepsSameDraftPrefix(active.draft, input)) {
      return; // Keep preparing the next gear while the user completes the phrase.
    }

    const version = ++anticipationGenerationRef.current;
    active?.controller.abort();
    activeAnticipationRef.current = null;
    anticipationAbortRef.current = null;
    setAnticipationPreview(null);
    if (!allowed || !session || !conversationId) return;

    const history = previewContext(messages);
    const timer = window.setTimeout(() => {
      if (version !== anticipationGenerationRef.current) return;
      const controller = new AbortController();
      anticipationAbortRef.current = controller;
      activeAnticipationRef.current = {
        mode, draft: mode === "typing" ? input : "", conversationId,
        token: session.sessionToken, controller, version
      };
      lastAnticipationStartedAtRef.current = Date.now();
      setAnticipationPreview({ mode, status: "preparing" });
      void requestOmniAnticipation({
        mode, draft: mode === "typing" ? input : "", history,
        language: mode === "typing" ? detectVoiceLanguage(input) : "es",
        sessionToken: session.sessionToken, signal: controller.signal
      }).then(result => {
        if (controller.signal.aborted || version !== anticipationGenerationRef.current) return;
        setAnticipationPreview(
          result.status === "ready" && result.preview
            ? { mode, status: "ready", text: result.preview }
            : result.status === "unavailable"
              ? { mode, status: "unavailable" }
              : null
        );
      }).catch(() => {
        if (!controller.signal.aborted && version === anticipationGenerationRef.current)
          setAnticipationPreview({ mode, status: "unavailable" });
      });
    }, anticipationDelay(mode, lastAnticipationStartedAtRef.current, Date.now()));

    return () => {
      window.clearTimeout(timer);
      const running = activeAnticipationRef.current;
      const latest = livePreviewContextRef.current;
      const sameOngoingThought = running?.version === version &&
        running.mode === "typing" && latest.active &&
        latest.conversationId === running.conversationId &&
        latest.token === running.token &&
        keepsSameDraftPrefix(running.draft, latest.draft);
      if (!sameOngoingThought) running?.controller.abort();
    };
  }, [input, messages, session?.sessionToken, conversationId, isLoading, imageBusy,
      attachment, cameraOpen, microphoneStatus, anticipationEnabled, lastResponse?.id]);

  useEffect(() => () => activeAnticipationRef.current?.controller.abort(), []);

  function toggleAnticipation() {
    const next = !anticipationEnabled;
    setAnticipationEnabled(next);
    if (!next) {
      ++anticipationGenerationRef.current;
      anticipationAbortRef.current?.abort();
      setAnticipationPreview(null);
    }
    try { window.localStorage.setItem("omnisynia.anticipacion.enabled", String(next)); }
    catch { /* Session-local preference still works. */ }
  }

  function stopLocation() {
    if (locationWatchRef.current !== null) {
      navigator.geolocation.clearWatch(locationWatchRef.current);
      locationWatchRef.current = null;
    }
    setLocation(null);
    setLocationStatus("inactive");
  }

  function activateLocation() {
    if (!("geolocation" in navigator)) {
      setLocationStatus("unavailable");
      return;
    }

    setLocationStatus("requesting");

    try {
      locationWatchRef.current = navigator.geolocation.watchPosition(
        (position) => {
          setLocation({
            latitude: Math.round(position.coords.latitude * 100) / 100,
            longitude: Math.round(position.coords.longitude * 100) / 100,
            accuracy: Math.round(position.coords.accuracy),
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC",
            updatedAt: new Date(position.timestamp).toISOString()
          });
          setLocationStatus("active");
        },
        (locationError) => {
          if (locationWatchRef.current !== null) {
            navigator.geolocation.clearWatch(locationWatchRef.current);
            locationWatchRef.current = null;
          }
          setLocation(null);
          setLocationStatus(locationError.code === 1 ? "denied" : "unavailable");
        },
        {
          enableHighAccuracy: false,
          maximumAge: 60_000,
          timeout: 15_000
        }
      );
    } catch {
      setLocationStatus("unavailable");
    }
  }

  function stopListening() {
    if (speechRecognitionRef.current) {
      try {
        speechRecognitionRef.current.stop();
      } catch {
        speechRecognitionRef.current.abort();
      }
      speechRecognitionRef.current = null;
    }
    setMicrophoneStatus("inactive");
  }

  function startListening() {
    const speechWindow = window as SpeechWindow;
    const Recognition = speechWindow.SpeechRecognition ?? speechWindow.webkitSpeechRecognition;

    if (!Recognition) {
      setMicrophoneStatus("unavailable");
      return;
    }

    if (speechRecognitionRef.current) {
      stopListening();
      return;
    }

    const recognition = new Recognition();
    recognition.lang = navigator.language.toLowerCase().startsWith("en") ? "en-US" : "es-ES";
    recognition.continuous = false;
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;
    speechRecognitionRef.current = recognition;
    setMicrophoneStatus("listening");

    recognition.onresult = (speechEvent) => {
      const result = speechEvent.results[speechEvent.results.length - 1];
      const transcript = result?.[0]?.transcript?.trim();
      if (transcript) {
        voiceInputRef.current = true;
        setInput((current) => `${current}${current.trim() ? " " : ""}${transcript}`);
      }
    };

    recognition.onerror = (speechError) => {
      speechRecognitionRef.current = null;
      setMicrophoneStatus(
        speechError.error === "not-allowed" || speechError.error === "service-not-allowed"
          ? "denied"
          : speechError.error === "audio-capture"
            ? "unavailable"
            : "error"
      );
    };

    recognition.onend = () => {
      speechRecognitionRef.current = null;
      setMicrophoneStatus((current) =>
        current === "denied" || current === "unavailable" || current === "error"
          ? current
          : "inactive"
      );
    };

    try {
      recognition.start();
    } catch {
      speechRecognitionRef.current = null;
      setMicrophoneStatus("error");
    }
  }

  function stopAudio() {
    voiceRequestRef.current += 1;
    audioRef.current?.pause();
    audioRef.current = null;
    if (audioUrlRef.current) {
      URL.revokeObjectURL(audioUrlRef.current);
      audioUrlRef.current = null;
    }
    setIsSpeaking(false);
  }

  async function speakText(
    content: string,
    profile: AiVoiceProfile = voiceProfile,
    localPreview = false
  ): Promise<boolean> {
    const spokenText = content
      .split(/\n\s*Fuentes:/i)[0]
      .replace(/https?:\/\/\S+/g, "")
      .replace(/[#*_]/g, "")
      .trim()
      .slice(0, 4_000);

    if (!spokenText) return false;
    const language = detectVoiceLanguage(spokenText);

    stopAudio();
    const voiceRequest = voiceRequestRef.current;
    setError(null);
    setIsSpeaking(true);

    if (session) {
      try {
        // A preview MUST use our own local engine, never an external TTS fallback.
        const request = {
          text: spokenText,
          voiceProfile: profile,
          voiceLanguage: language,
          sessionToken: session.sessionToken
        };
        const preview = localPreview
          ? await synthesizeAiSpeechDetailed({ ...request, localOnly: true })
          : undefined;
        const audioBlob = preview?.audio ?? await synthesizeAiSpeech(request);
        if (voiceRequestRef.current !== voiceRequest) return false;
        if (preview) {
          const voiceLabel = verifiedLocalVoiceLabel(preview.model, preview.voice, profile);
          setVoicePreviewNotice("Escuchando " + voiceLabel +
            ". Voz sintética natural, sin copiar la voz de personas.");
        }

        const audioUrl = URL.createObjectURL(audioBlob);
        const audio = new Audio(audioUrl);
        audioRef.current = audio;
        audioUrlRef.current = audioUrl;
        const finishAudio = () => {
          if (audioRef.current === audio) audioRef.current = null;
          if (audioUrlRef.current === audioUrl) {
            URL.revokeObjectURL(audioUrl);
            audioUrlRef.current = null;
          }
          setIsSpeaking(false);
        };
        audio.onended = finishAudio;
        audio.onerror = finishAudio;
        await audio.play();
        return true;
      } catch (cause) {
        if (voiceRequestRef.current !== voiceRequest) return false;
        if (localPreview) {
          setVoicePreviewNotice(cause instanceof AiClientError
            ? cause.message : "No se pudo reproducir la voz local. Comprueba la conexión.");
        } else {
          setError("No se pudo generar la voz natural. Comprueba la conexión e inténtalo de nuevo.");
        }
      }
    } else {
      setError("La voz natural estará disponible cuando termine de iniciarse tu sesión.");
    }

    setIsSpeaking(false);
    return false;
  }

  async function toggleMemory() {
    if (!session || memoryBusy) return;
    const nextEnabled = !memoryEnabled;
    setMemoryBusy(true);
    setMemoryNotice(null);
    try {
      const consent = await setMemoryConsent(session.sessionToken, nextEnabled);
      setMemoryEnabled(consent.includes("user") || consent.includes("project"));
      setSharedContinuity(consent.includes("conversation"));
      setMemoryNotice(
        consent.length > 0
          ? "Memoria compartida activada: recuerdos y contexto de tus conversaciones en los dispositivos de esta cuenta."
          : "Memoria desactivada. Los recuerdos permanecen guardados, pero OMNISYNIA no los usará."
      );
    } catch (memoryError) {
      setMemoryNotice(memoryError instanceof Error ? memoryError.message : "No se pudo cambiar la memoria.");
    } finally {
      setMemoryBusy(false);
    }
  }

  async function toggleSharedContinuity() {
    if (!session || memoryBusy) return;
    setMemoryBusy(true);
    try {
      const consent = await setConversationContinuity(session.sessionToken, !sharedContinuity);
      setSharedContinuity(consent.includes("conversation"));
      setMemoryEnabled(consent.includes("user") || consent.includes("project"));
      setMemoryNotice(consent.includes("conversation")
        ? "Tus conversaciones comparten contexto entre los dispositivos de esta cuenta. Cada una conserva su tema."
        : "Contexto entre conversaciones desactivado. Cada chat mantiene su propio historial.");
    } catch (error) {
      setMemoryNotice(error instanceof Error ? error.message : "No se pudo cambiar la continuidad.");
    } finally { setMemoryBusy(false); }
  }

  async function submitMemory(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const content = memoryDraft.trim();
    if (!session || !memoryEnabled || memoryBusy || !content) return;
    setMemoryBusy(true);
    setMemoryNotice(null);
    try {
      if (editingMemoryId) {
        const updated = await updateMemory(session.sessionToken, editingMemoryId, content);
        setMemories((current) => current.map((memory) => memory.id === updated.id ? updated : memory));
        setMemoryNotice("Recuerdo corregido.");
      } else {
        const created = await saveMemory(session.sessionToken, {
          category: memoryCategory,
          content,
          source: "memory-panel"
        });
        setMemories((current) => [...current, created]);
        setMemoryNotice("Recuerdo guardado y disponible para futuras conversaciones.");
      }
      setMemoryDraft("");
      setEditingMemoryId(null);
    } catch (memoryError) {
      setMemoryNotice(memoryError instanceof Error ? memoryError.message : "No se pudo guardar el recuerdo.");
    } finally {
      setMemoryBusy(false);
    }
  }

  function beginMemoryEdit(memory: MemoryRecord) {
    setEditingMemoryId(memory.id);
    setMemoryCategory(memory.category);
    setMemoryDraft(memory.content);
    setMemoryNotice("Edita el texto y pulsa Guardar cambio.");
  }

  function cancelMemoryEdit() {
    setEditingMemoryId(null);
    setMemoryDraft("");
    setMemoryNotice(null);
  }

  async function removeMemory(memory: MemoryRecord) {
    if (!session || memoryBusy) return;
    if (!window.confirm("¿Eliminar este recuerdo de OMNISYNIA?")) return;
    setMemoryBusy(true);
    try {
      await deleteStoredMemory(session.sessionToken, memory.id);
      setMemories((current) => current.filter((item) => item.id !== memory.id));
      if (editingMemoryId === memory.id) cancelMemoryEdit();
      setMemoryNotice("Recuerdo eliminado.");
    } catch (memoryError) {
      setMemoryNotice(memoryError instanceof Error ? memoryError.message : "No se pudo eliminar el recuerdo.");
    } finally {
      setMemoryBusy(false);
    }
  }

  async function removeAllMemory() {
    if (!session || memoryBusy || memories.length === 0) return;
    if (!window.confirm("¿Eliminar todos los recuerdos y desactivar la memoria?")) return;
    setMemoryBusy(true);
    try {
      await deleteAllMemories(session.sessionToken);
      setMemories([]);
      setMemoryEnabled(false);
      setSharedContinuity(false);
      setMemoryDraft("");
      setEditingMemoryId(null);
      setMemoryNotice("Todos los recuerdos se eliminaron y la memoria quedó desactivada.");
    } catch (memoryError) {
      setMemoryNotice(memoryError instanceof Error ? memoryError.message : "No se pudo borrar la memoria.");
    } finally {
      setMemoryBusy(false);
    }
  }

  async function rememberVoicePreference(profile: AiVoiceProfile) {
    if (!session || !memoryEnabled) return;
    const content = profile === "feminine"
      ? "Prefiero que OMNISYNIA use la voz femenina natural."
      : "Prefiero que OMNISYNIA use la voz masculina natural.";
    const existing = memories.find((memory) => memory.source === "voice-preference");
    try {
      if (existing) {
        const updated = await updateMemory(session.sessionToken, existing.id, content);
        setMemories((current) => current.map((memory) => memory.id === updated.id ? updated : memory));
      } else {
        const created = await saveMemory(session.sessionToken, {
          category: "user",
          content,
          source: "voice-preference"
        });
        setMemories((current) => [...current, created]);
      }
    } catch {
      setMemoryNotice("La voz se guardó en este dispositivo, pero no en la memoria de la cuenta.");
    }
  }

  function selectVoice(profile: AiVoiceProfile) {
    stopAudio();
    setVoicePreviewNotice("");
    setVoiceProfile(profile);
    try {
      window.localStorage.setItem("omnisynia.voiceProfile", profile);
    } catch {
      // La voz seguirá activa durante esta sesión aunque el navegador bloquee el almacenamiento.
    }
    setError(null);
    void rememberVoicePreference(profile);
  }

  function previewVoice(profile: AiVoiceProfile) {
    if (!session) {
      setVoicePreviewNotice("Inicia sesión para escuchar las voces de ONE CORE.");
      return;
    }
    setVoicePreviewNotice("Preparando una muestra de voz local, sin llamadas a OpenAI…");
    void speakText(VOICE_PREVIEW_ES[profile], profile, true);
  }

  function toggleVoiceOutput() {
    if (voiceOutputEnabled) {
      stopAudio();
      setVoiceOutputEnabled(false);
      return;
    }

    setError(null);
    setVoiceOutputEnabled(true);
  }

  async function startNewConversation() {
    ++anticipationGenerationRef.current;
    anticipationAbortRef.current?.abort();
    setAnticipationPreview(null);
    if (!session || isLoading || imageBusy) return;
    const conversation = await createConversation(session.sessionToken);
    setAttachment(null);
    setInput("");
    setConversationId(conversation.id);
    ++historyGenerationRef.current;
    rememberConversation(session.user.id, conversation.id);
    setMessages([greeting]);
    voiceInputRef.current = false;
    setLastResponse(null);
  }
  async function attachPhoto(file?: File) {
    if (!file || isLoading || imageBusy) return;
    const requestId = ++imageRequestRef.current;
    setImageBusy(true);
    setError(null);
    try {
      const image = await prepareChatImage(file);
      if (requestId === imageRequestRef.current) setAttachment(image);
    } catch (error) {
      if (requestId === imageRequestRef.current) setError(error instanceof Error ? error.message : "No se pudo adjuntar la foto.");
    } finally {
      if (requestId === imageRequestRef.current) setImageBusy(false);
    }
  }

  async function sendMessage(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    ++anticipationGenerationRef.current;
    anticipationAbortRef.current?.abort();
    setAnticipationPreview(null);

    const originalInput = input.trim() ? input : attachment ? "¿Qué aparece en esta foto?" : input;
    const sentImage = attachment;

    if (!originalInput.trim() || isLoading || sendingRef.current || imageBusy || !session || !conversationId) {
      return;
    }

    sendingRef.current = true;
    ++historyGenerationRef.current;
    const voiceInteraction = voiceOutputEnabled || voiceInputRef.current;
    voiceInputRef.current = false;
    const nextMessages: AiChatMessage[] = [
      ...messages,
      {
        role: "user",
        content: originalInput,
        image: sentImage ?? undefined
      }
    ];

    setMessages(nextMessages);
    setInput("");
    setAttachment(null);
    setIsLoading(true);
    setError(null);

    try {
      const response = await sendAiChat({
        conversationId,
        moduleId: "chat-omnisynia",
        sessionId: session.sessionId,
        sessionToken: session.sessionToken,
        organizationId: getActiveUltraOrganizationId() ?? undefined,
        messages: nextMessages,
        metadata: {
          locale: "es",
          platform: "web",
          interactionMode: voiceInteraction ? "voice" : "text",
          location: location
            ? {
                latitude: location.latitude,
                longitude: location.longitude,
                accuracy: location.accuracy,
                timezone: location.timezone,
                updatedAt: location.updatedAt
              }
            : undefined
        },
        userConsent: {
          privacyPolicyAccepted: true,
          termsAccepted: true,
          aiDisclosureAccepted: true,
          safetyPolicyAccepted: true,
          memoryOptIn: memoryEnabled || sharedContinuity
        }
      });

      // Muestra la respuesta en cuanto llega del motor IA. El guardado del
      // historial no debe ocultar una respuesta válida.
      setLastResponse(response);
      setMessages([
        ...nextMessages,
        {
          role: "assistant",
          content: response.message.content
        }
      ]);
      if (voiceOutputEnabled) void speakText(response.message.content);

      // ONE CORE saves the turn before returning. No second client-side write.
      // Preserve history during a rolling deployment against the previous backend.
      if (!response.conversationSaved) {
        try {
          await saveConversationMessage(session.sessionToken, conversationId, {
            role: "user", content: originalInput + (sentImage ? "\n[Foto adjunta en la sesión; no guardada en el historial.]" : "")
          });
          await saveConversationMessage(session.sessionToken, conversationId, response.message);
        } catch {
          setError("OMNISYNIA respondió, pero no se pudo guardar el historial.");
        }
      }
    } catch (err) {
      setMessages(messages);
      setInput(input);
      setAttachment(sentImage);
      setError(
        err instanceof AiClientError
          ? `${err.message} (${err.code})`
          : "No se pudo conectar con el motor IA."
      );
    } finally {
      sendingRef.current = false;
      setIsLoading(false);
    }
  }

  return (
    <section className="editor-shell" aria-labelledby="chat-omnisynia-title">
      <header className="editor-header">
        <div>
          <p className="eyebrow">CHAT PRINCIPAL · OMNISYNIA CORE</p>
          <h2 id="chat-omnisynia-title">Chat OMNISYNIA</h2>
          <p>
            Conversación general conectada al backend, OpenAI y la base Postgres real.
          </p>
        </div>
        <span className="status-pill">AI READY</span>
      </header>

      <div className="ai-panel">
        <div className="ai-panel-header">
          <div>
            <p className="eyebrow">MOTOR IA CONECTADO</p>
            <h3>Habla con OMNISYNIA</h3>
          </div>
          <div className="status-row" aria-label="Servicios conectados">
            <span className="status-pill">IDENTIDAD NEUTRAL</span>
            <span className="status-pill">INTERNET READY</span>
            <span className="status-pill">POSTGRES READY</span>
          </div>
        </div>

        <div className={`memory-panel ${memoryEnabled ? "active" : ""}`} aria-live="polite">
          <div>
            <strong>Memoria segura</strong>
            <span>
              {sharedContinuity && !memoryEnabled
                ? "Tus conversaciones comparten contexto. Los recuerdos manuales están desactivados."
                : memoryEnabled
                ? `Activada · ${memories.length} recuerdo${memories.length === 1 ? "" : "s"}. ${sharedContinuity ? "Contexto compartido entre tus conversaciones y dispositivos." : "Solo usa los recuerdos que guardes."}`
                : "Desactivada. OMNISYNIA no utilizará recuerdos personales."}
            </span>
            {memoryNotice ? <small>{memoryNotice}</small> : null}
          </div>
          <button
            aria-pressed={memoryEnabled}
            className="secondary-button memory-toggle"
            disabled={!session || memoryBusy}
            onClick={() => void toggleMemory()}
            type="button"
          >
            {memoryBusy ? "Procesando…" : memoryEnabled ? "Desactivar memoria" : "Activar memoria"}
          </button>
          <button type="button" className="secondary-button" aria-pressed={sharedContinuity}
            disabled={!session || memoryBusy} onClick={() => void toggleSharedContinuity()}>
            {sharedContinuity ? "Conversaciones conectadas" : "Conectar conversaciones"}
          </button>
        </div>

        {memoryEnabled ? (
          <details className="memory-manager">
            <summary>Gestionar recuerdos ({memories.length})</summary>
            <p className="memory-privacy">
              Guarda preferencias o proyectos. El GPS no se guarda como recuerdo automático. Puedes desconectar tus conversaciones o borrar recuerdos cuando quieras.
            </p>
            <form className="memory-form" onSubmit={submitMemory}>
              <label>
                Tipo de recuerdo
                <select
                  disabled={Boolean(editingMemoryId)}
                  onChange={(event) => setMemoryCategory(event.target.value as MemoryCategory)}
                  value={memoryCategory}
                >
                  <option value="user">Sobre mí y preferencias</option>
                  <option value="project">Proyecto</option>
                </select>
              </label>
              <WritingTextarea label="Recuerdo" key={editingMemoryId ?? "new-memory"}
                  maxLength={2000}
                  onValueChange={text => setMemoryDraft(text)}
                  placeholder="Ejemplo: Prefiero respuestas breves y claras."
                  rows={3}
                  value={memoryDraft}
                />
              <div className="memory-form-actions">
                <button className="primary-button" disabled={memoryBusy || !memoryDraft.trim()} type="submit">
                  {editingMemoryId ? "Guardar cambio" : "Guardar recuerdo"}
                </button>
                {editingMemoryId ? (
                  <button className="secondary-button" onClick={cancelMemoryEdit} type="button">Cancelar</button>
                ) : null}
              </div>
            </form>
            <div className="memory-list" aria-label="Recuerdos guardados">
              {memories.length === 0 ? (
                <p className="memory-empty">Todavía no hay recuerdos guardados.</p>
              ) : memories.map((memory) => (
                <article key={memory.id}>
                  <span>{memoryCategoryLabel(memory.category)}</span>
                  <p>{memory.content}</p>
                  <div>
                    <button className="secondary-button" onClick={() => beginMemoryEdit(memory)} type="button">Corregir</button>
                    <button className="danger-button" onClick={() => void removeMemory(memory)} type="button">Eliminar</button>
                  </div>
                </article>
              ))}
            </div>
            {memories.length > 0 ? (
              <button className="danger-button memory-delete-all" onClick={() => void removeAllMemory()} type="button">
                Borrar todos los recuerdos
              </button>
            ) : null}
          </details>
        ) : null}

        <div className={`location-panel ${locationStatus === "active" ? "active" : ""}`} aria-live="polite">
          <div>
            <strong>Ubicación para respuestas cercanas</strong>
            <span>
              {locationStatus === "active"
                ? "Activa mientras OMNISYNIA está abierta. La zona aproximada se envía a OpenAI para responder y no se guarda."
                : locationStatus === "requesting"
                  ? "Esperando el permiso del dispositivo…"
                  : locationStatus === "denied"
                    ? "Permiso bloqueado. Debes permitir la ubicación en el aviso del navegador."
                    : locationStatus === "unavailable"
                      ? "No se pudo obtener la ubicación de este dispositivo."
                      : "Desactivada. Al activarla, la zona aproximada se enviará a OpenAI para clima y lugares cercanos; no se guardará."}
            </span>
          </div>
          <button
            className="secondary-button location-button"
            disabled={locationStatus === "requesting"}
            onClick={locationStatus === "active" ? stopLocation : activateLocation}
            type="button"
          >
            {locationStatus === "active"
              ? "Desactivar ubicación"
              : locationStatus === "requesting"
                ? "Solicitando permiso…"
                : "Activar ubicación"}
          </button>
        </div>

        <div
          className={`voice-panel ${microphoneStatus === "listening" || voiceOutputEnabled ? "active" : ""}`}
          aria-live="polite"
        >
          <div>
            <strong>Voz OMNISYNIA</strong>
            <span>
              {microphoneStatus === "listening"
                ? "Escuchando… Habla ahora."
                : microphoneStatus === "denied"
                  ? "Permiso bloqueado. Pulsa Permitir cuando el navegador solicite el micrófono."
                  : microphoneStatus === "unavailable"
                    ? "El reconocimiento de voz no está disponible en este dispositivo."
                    : microphoneStatus === "error"
                      ? "No pude reconocer la voz. Vuelve a intentarlo."
                      : voiceOutputEnabled
                        ? "Micrófono listo. OMNISYNIA responderá por escrito y en voz."
                        : "El micrófono solo se activa cuando pulsas Hablar."}
            </span>
            <small className="voice-disclosure">Elige una voz femenina o masculina natural. Al activar la voz, OMNISYNIA leerá el contenido completo de cada respuesta en el mismo idioma en que responda.</small>
          </div>
          <div className="voice-controls" role="group" aria-label="Controles de voz OMNISYNIA">
            <button
              aria-pressed={voiceProfile === "feminine"}
              className={`secondary-button voice-button voice-choice ${voiceProfile === "feminine" ? "selected" : ""}`}
              onClick={() => selectVoice("feminine")}
              type="button"
            >
              Femenina suave
            </button>
            <button
              aria-pressed={voiceProfile === "masculine"}
              className={`secondary-button voice-button voice-choice ${voiceProfile === "masculine" ? "selected" : ""}`}
              onClick={() => selectVoice("masculine")}
              type="button"
            >
              Masculina suave
            </button>
            <button type="button" className="secondary-button voice-button"
              disabled={isSpeaking || !session}
              onClick={() => previewVoice("feminine")}>▶ Escuchar voz femenina</button>
            <button type="button" className="secondary-button voice-button"
              disabled={isSpeaking || !session}
              onClick={() => previewVoice("masculine")}>▶ Escuchar voz masculina</button>
            {isSpeaking && <button type="button" className="secondary-button voice-button"
              onClick={stopAudio}>■ Detener audio</button>}
            <button
              aria-pressed={microphoneStatus === "listening"}
              className={`secondary-button voice-button ${microphoneStatus === "listening" ? "listening" : ""}`}
              onClick={microphoneStatus === "listening" ? stopListening : startListening}
              type="button"
            >
              {microphoneStatus === "listening" ? "Detener micrófono" : "Hablar"}
            </button>
            <button
              aria-pressed={voiceOutputEnabled}
              className="secondary-button voice-button"
              onClick={toggleVoiceOutput}
              type="button"
            >
              {voiceOutputEnabled ? (isSpeaking ? "Detener y silenciar" : "Silenciar respuestas") : "Activar voz"}
            </button>
          </div>
          <small className="voice-disclosure">Prueba de sonido local: Dora (femenina) y Alex (masculina) cuando el HP utiliza Kokoro.
            Si el servidor no está conectado, se mostrará un error sin recurrir a una voz externa.</small>
          {voicePreviewNotice && <p role="status" className="voice-disclosure">{voicePreviewNotice}</p>}
        </div>

        <button
          className="secondary-button"
          disabled={!session || isLoading || imageBusy}
          onClick={() => void startNewConversation()}
          type="button"
        >
          Nueva conversación
        </button>

        <div className="ai-response" aria-label="Conversación OMNISYNIA">
          {messages.map((message, index) => (
            <p key={`${message.role}-${index}`}>
              <strong>{message.role === "user" ? "Tú" : "OMNISYNIA"}:</strong>{" "}
              <MessageContent content={message.content} isAssistant={message.role === "assistant"} />
              {message.image ? <img src={message.image} alt="Foto enviada al chat" style={{ display: "block", maxWidth: "100%", width: 280, maxHeight: 320, objectFit: "contain", marginTop: 12, borderRadius: 12 }} /> : null}
            </p>
          ))}
        </div>

        {error ? (
          <p className="error-message" role="alert">{error}</p>
        ) : null}

        {cameraOpen ? <ChatCamera
          onClose={() => setCameraOpen(false)}
          onCapture={file => { setCameraOpen(false); void attachPhoto(file); }}
          onNativeCamera={() => { setCameraOpen(false); cameraInputRef.current?.click(); }}
          onChooseFile={() => { setCameraOpen(false); galleryInputRef.current?.click(); }}
        /> : null}
        <form onSubmit={sendMessage}>
          <div className="status-row" role="group" aria-label="Adjuntar una foto al chat">
            <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" hidden
              aria-label="Tomar foto para el chat" onChange={event => {
                const file = event.currentTarget.files?.[0];
                event.currentTarget.value = "";
                void attachPhoto(file);
              }} />
            <input ref={galleryInputRef} type="file" accept="image/*" hidden
              aria-label="Elegir foto para el chat" onChange={event => {
                const file = event.currentTarget.files?.[0];
                event.currentTarget.value = "";
                void attachPhoto(file);
              }} />
            <button type="button" className="secondary-button" disabled={isLoading || imageBusy}
              onClick={() => {
                if (typeof navigator.mediaDevices?.getUserMedia === "function" && typeof HTMLDialogElement !== "undefined" &&
                    typeof HTMLDialogElement.prototype.showModal === "function") setCameraOpen(true);
                else cameraInputRef.current?.click();
              }}>Cámara</button>
            <button type="button" className="secondary-button" disabled={isLoading || imageBusy}
              onClick={() => galleryInputRef.current?.click()}>Adjuntar foto</button>
          </div>
          {imageBusy ? <p role="status">Preparando foto…</p> : null}
          {attachment ? (
            <figure aria-label="Foto adjunta al mensaje" style={{ margin: "16px 0" }}>
              <img src={attachment} alt="Vista previa de la foto adjunta" style={{ maxWidth: "100%", width: 240, maxHeight: 280, objectFit: "contain", borderRadius: 12 }} />
              <figcaption>Foto lista. Puedes añadir una pregunta y pulsar Enviar.</figcaption>
              <button type="button" className="secondary-button" disabled={isLoading || imageBusy}
                onClick={() => setAttachment(null)}>Quitar foto</button>
              <p><small>La foto se usa en este envío y no se guarda en el historial.</small></p>
            </figure>
          ) : null}
          <WritingTextarea label="Escribe tu mensaje" key={conversationId}
              value={input}
              onValueChange={text => setInput(text)}
              maxLength={2000}
              rows={5}
              aria-label="Mensaje para OMNISYNIA"
              disabled={isLoading}
            />
          <div className="omni-anticipation-control">
            <button type="button" aria-pressed={anticipationEnabled}
              className={anticipationEnabled ? "omni-anticipation-toggle active" : "omni-anticipation-toggle"}
              onClick={toggleAnticipation}>
              <span aria-hidden="true">⚡</span> OMNI ANTICIPACIÓN
              <strong>{anticipationEnabled ? "Activada" : "Pausada"}</strong>
            </button>
            <small>Prepara una posible respuesta mientras escribes y sigue el hilo cuando se hace una pausa.</small>
          </div>
          {anticipationEnabled && anticipationPreview ? (
            <aside role="status" aria-live="polite" className="omni-anticipation-preview"
              aria-label="Anticipo provisional de OMNISYNIA">
              <strong>{anticipationPreview.mode === "typing"
                ? "Respuesta que OMNISYNIA está anticipando"
                : "OMNISYNIA continúa el mismo tema"}</strong>
              {anticipationPreview.status === "preparing" ? (
                <p>ONE CORE prepara el siguiente paso…</p>
              ) : anticipationPreview.status === "ready" ? (
                <p>{anticipationPreview.text}</p>
              ) : (
                <p>La anticipación local no está disponible. El chat normal sigue funcionando.</p>
              )}
              <small>Vista previa provisional. No se envía, no se guarda y no consume créditos.</small>
            </aside>
          ) : null}

          <button className="primary-button" disabled={isInitializing || isLoading || imageBusy || !session || (!input.trim() && !attachment)} type="submit">
            {isInitializing ? "Cargando historial..." : isLoading ? "Pensando..." : "Enviar a OMNISYNIA"}
          </button>
        </form>

        {lastResponse && session?.user.role === "creator" ? (
          <article className="ai-response" aria-label="Estado técnico del motor IA">
            <header>
              <span>{lastResponse.provider}</span>
              <span>{lastResponse.model}</span>
              <span>{lastResponse.route.moduleId}</span>
              <span>Sesión: {lastResponse.session.userMode}</span>
            </header>
          </article>
        ) : null}
      </div>
    </section>
  );
}

export const chatOmnisyniaClientModule: OmnisyniaClientModule = {
  id: "chat-omnisynia",
  displayName: "Chat OMNISYNIA",
  description: "Chat principal general de OMNISYNIA One Intelligence.",
  tier: "core",
  status: "available",
  Component: ChatOmnisynia
};
